import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import type { AccountType } from '@/api/schema';
import { isLanguageSupported } from '@/configuration/lang/lang';
import { usePageTracking } from '@/hooks/usePageTracking';
import {
    DEFAULT_ROUTE,
    NO_PERMISSIONS,
    NOT_AVAILABLE,
    NOT_FOUND,
    permissionsMap,
    routes,
    SIGNUP,
} from '@/routes/Router';
import { checkPermission, useUserStore } from '@/stores/userStore';

const isKnownRoute = (pathname: string): boolean =>
    routes.some(route => pathname === route || pathname.startsWith(`${route}/`));

const getBasePath = (pathWithQuery: string): string => pathWithQuery.split('?')[0];

const getRedirectPath = (pathname: string, account: AccountType | null, search: string): string => {
    if (!account?.permissions) {
        return NO_PERMISSIONS;
    }

    if (!isLanguageSupported(account?.address.countryCode)) {
        return NOT_AVAILABLE;
    }

    if (!account?.isOnboarded) {
        return SIGNUP;
    }

    if (pathname === '/') {
        return `${DEFAULT_ROUTE}${search}`;
    }

    if (!isKnownRoute(pathname)) {
        return NOT_FOUND;
    }

    return `${pathname}${search}`;
};

const DefaultRedirect = () => {
    const { pathname, search } = useLocation();
    const { account } = useUserStore();
    const navigate = useNavigate();

    usePageTracking();

    useEffect(() => {
        const redirectPath = getRedirectPath(pathname, account, search);
        const requiredPermission = permissionsMap.get(getBasePath(redirectPath)) || '';
        const isUserAllowed = checkPermission(account, requiredPermission);

        navigate(isUserAllowed ? redirectPath : NO_PERMISSIONS);
    }, [pathname, account, navigate]);

    return null;
};

export default DefaultRedirect;
